import Button from '@material-ui/core/Button';
import { Calendar } from '@material-ui/pickers';
import _ from 'lodash';
import moment from 'moment-timezone';
import React from 'react';
import { Popover, PopoverBody } from '../../popovers';
import DateComponent from '../DateComponent';
import classNames from 'classnames';

let defaultTimeframeCaptions = {
   today: 'Today',
   tomorrow: 'Tomorrow',
   thisWeek: 'This Week',
   nextWeek: 'Next Week',
   thisMonth: 'This Month',
   nextMonth: 'Next Month',
   later: 'Later',
   custom: 'Specified Date'
};

function buildValues(startOfPeriod, timezone) {
   const now = timezone ? moment.tz(timezone) : moment();

   let o = null;

   if (startOfPeriod) {
      o = {
         yesterday: now.clone().add(-1, 'days').startOf('day'),
         today: now.clone().startOf('day'),
         tomorrow: now.clone().add(1, 'days').startOf('day'),
         thisWeek: now.clone().startOf('week'),
         thisMonth: now.clone().startOf('month'),
         nextWeek: now.clone().endOf('week').add(1, 'days').startOf('week'),
         nextMonth: now.clone().endOf('month').add(1, 'days').startOf('month')
      };
   } else {
      o = {
         yesterday: now.clone().add(-1, 'days').endOf('day'),
         today: now.clone().endOf('day'),
         tomorrow: now.clone().add(1, 'days').endOf('day'),
         thisWeek: now.clone().endOf('week'),
         thisMonth: now.clone().endOf('month'),
         nextWeek: now.clone().endOf('week').add(7, 'days'),
         nextMonth: now.clone().endOf('month').add(1, 'days').endOf('month')
      };
   }

   return o;
}

function buildBoundaries(timezone) {
   const now = timezone ? moment.tz(timezone) : moment();

   return {
      start: {
         yesterday: now.clone().add(-1, 'days').startOf('day'),
         today: now.clone().startOf('day'),
         tomorrow: now.clone().add(1, 'days').startOf('day'),
         thisWeek: now.clone().startOf('week'),
         thisMonth: now.clone().startOf('month'),
         nextWeek: now.clone().endOf('week').add(1, 'days').startOf('week'),
         nextMonth: now.clone().endOf('month').add(1, 'days').startOf('month')
      },
      end: {
         yesterday: now.clone().add(-1, 'days').endOf('day'),
         today: now.clone().endOf('day'),
         tomorrow: now.clone().add(1, 'days').endOf('day'),
         thisWeek: now.clone().endOf('week'),
         thisMonth: now.clone().endOf('month'),
         nextWeek: now.clone().endOf('week').add(7, 'days'),
         nextMonth: now.clone().endOf('month').add(1, 'days').endOf('month')
      }
   };
}

function buildTimeFramePeriod(startOfPeriod, currentDate, timezone, boundaries, timeframe, timeframeCaptions) {
   var dateCaption = '',
      periods = ['nextMonth', 'thisMonth', 'nextWeek', 'thisWeek', 'tomorrow', 'today', 'yesterday'],
      periodEnd = null,
      periodStart = null,
      instant;

   if (!boundaries) {
      boundaries = getTimeFrameMoments(timezone);
   }

   if (currentDate == null) {
      return { periodStart: null, periodEnd: null, timeframe: 'later', dateCaption: timeframeCaptions['later'] };
   }

   instant = moment.isMoment(currentDate) ? currentDate : moment(currentDate);

   if (!timeframe) {
      if (startOfPeriod) {
         _.forEach(periods.reverse(), function (p) {
            if (instant.isSameOrAfter(boundaries.start[p], 'second')) {
               if (instant.isSame(boundaries.start[p], 'second')) {
                  timeframe = p;
               }
            }
         });
      } else {
         _.forEach(periods, function (p) {
            if (instant.isSameOrBefore(boundaries.end[p], 'second')) {
               if (instant.isSame(boundaries.end[p], 'second')) {
                  timeframe = p;
               }
            }
         });
      }
   }

   if (!timeframe) {
      timeframe = 'custom';
   }

   periodEnd = boundaries.end[timeframe] || instant.clone().endOf('day');
   periodStart = boundaries.start[timeframe] || instant.clone().startOf('day');

   dateCaption = timeframe == 'custom' ? instant.format('ll') : timeframeCaptions[timeframe];

   if (typeof dateCaption === 'undefined') {
      dateCaption = instant.format('ll');
   }

   return { periodEnd: periodEnd, periodStart: periodStart, timeframe: timeframe, dateCaption: dateCaption };
}

class DateSelector extends DateComponent {
   constructor(props) {
      super(props);

      const { currentDate, displayZone } = this.buildDateTimeAndZone(this.props);

      var o = this.buildCurrentTimeFrame(currentDate);

      this.state = _.merge(o, {
         editingDate: false,
         displayZone: displayZone,
         currentDate: currentDate,
         original: this.props.currentDate,
         anchorEl: null,
         broadcast: false
      });
   }

   buildCurrentTimeFrame(date, timeframe) {
      const { startOfPeriod, timeframeCaptions } = this.props;

      var b = buildBoundaries(),
         o = buildTimeFramePeriod(startOfPeriod, date, null, b, timeframe, timeframeCaptions);

      return o;
   }

   buildDateTimeAndZone(props) {
      var currentDate = this.buildInput(props.currentDate, props.zone, false, true),
         displayZone = this.calculateDisplayZone(currentDate, props.zone);

      return { currentDate, displayZone };
   }

   UNSAFE_componentWillReceiveProps(nextProps) {
      if (this.state.original != nextProps.currentDate) {
         const { currentDate, displayZone, lastbuild } = this.buildDateTimeAndZone(nextProps);

         var o = this.buildCurrentTimeFrame(currentDate);

         this.setState(
            _.merge(o, { currentDate: currentDate, displayZone: displayZone, editingDate: false, original: nextProps.currentDate })
         );
      }
   }

   componentDidUpdate() {
      if (this.state.broadcast) {
         const { currentDate, periodStart, periodEnd } = this.state;

         const d = this.buildOutput(currentDate, this.props.zone);
         const start = this.buildOutput(periodStart, this.props.zone);
         const end = this.buildOutput(periodEnd, this.props.zone);
         this.props.onChange({ currentDate: d, start, end });
         this.setState({ broadcast: false });
      }
   }

   handleDateChange = (date) => {
      let d = moment.isMoment(date) ? date : moment(date);

      const { startOfPeriod } = this.props;
      if (startOfPeriod) {
         d.startOf('day');
      } else {
         d.endOf('day');
      }

      var o = this.buildCurrentTimeFrame(d, 'custom'),
         displayZone = this.calculateDisplayZone(d, this.props.zone);

      this.setState(_.merge(o, { editingDate: false, currentDate: d, displayZone: displayZone, broadcast: true }));
   };

   toggleEditingDate = () => {
      const { editingDate } = this.state;
      this.setState({ editingDate: !editingDate });
   };

   handleTimeFrameClick = (e, data) => {
      const { startOfPeriod } = this.props;

      let v = buildValues(startOfPeriod),
         date = v[data.value] ? v[data.value] : null,
         o = this.buildCurrentTimeFrame(date, data.value);

      this.setState(_.merge(o, { editingDate: false, currentDate: date, broadcast: true }));
   };

   renderCalendar() {
      const { currentDate, displayZone } = this.state;

      var divStyle = { width: '75%' };

      const localDate = currentDate ? moment(currentDate) : moment();

      return <div className='col-sm-6 col-md-6 col-ls-6'>{<Calendar date={localDate} onChange={this.handleDateChange} />}</div>;
   }

   renderTimeFrames() {
      var ommissions = this.props.ommissions;
      var timeframeCaptions = this.props.timeframeCaptions;

      return (
         <div className='timeframes'>
            {_.keys(_.omit(timeframeCaptions, ommissions)).map((key) => (
               <Button
                  key={key}
                  onClick={(e) => this.handleTimeFrameClick(e, { value: key })}
                  className='timeframe-btn transform-none'
                  color='primary'>
                  {timeframeCaptions[key]}
               </Button>
            ))}
         </div>
      );
   }

   render() {
      const { currentDate, editingDate, dateCaption, anchorEl } = this.state;
      const { readOnly, id, label, useRow, useButton } = this.props;

      let captionToShow =
         dateCaption.startsWith('T') || dateCaption.startsWith('N') || dateCaption.startsWith('L')
            ? dateCaption.toLowerCase()
            : dateCaption;
      let popupTarget = id ? id + '-set-date-spn' : 'set-date-spn';
      let showLabel = label != null;

      return (
         <div className='DateSelector'>
            {readOnly && (
               <div className={classNames('date-control', { dateRow: useRow }, { dateColumn: !useRow })}>
                  {showLabel && <label>{label}</label>}
                  <span>{captionToShow}</span>
               </div>
            )}
            {!readOnly && !useButton && (
               <div className={classNames('date-control', { dateRow: useRow }, { dateColumn: !useRow })}>
                  {showLabel && <label>{label}</label>}
                  <span id={popupTarget} className='filter-link' onClick={this.toggleEditingDate}>
                     {captionToShow}
                  </span>
               </div>
            )}
            {!readOnly && useButton && (
               <Button variant='outlined' className='jr-btn' onClick={this.toggleEditingDate}>
                  <span id={popupTarget}>{captionToShow}</span>
               </Button>
            )}
            {editingDate && (
               <Popover
                  placement='bottom'
                  isOpen={editingDate}
                  target={popupTarget}
                  className='task-startingdate-selector-popover'
                  toggle={this.toggleEditingDate}>
                  <PopoverBody>
                     <div className='card-text row' style={Object.assign({}, { maxWidth: '400px' })}>
                        {this.renderTimeFrames()}
                        {this.renderCalendar()}
                     </div>
                  </PopoverBody>
               </Popover>
            )}
         </div>
      );
   }
}

DateSelector.defaultProps = {
   timeframeCaptions: defaultTimeframeCaptions,
   ommissions: ['thisMonth', 'thisWeek', 'nextWeek', 'nextMonth', 'custom', 'later'],
   currentDate: new Date(),
   onChange: () => {},
   zone: 'Etc/GMT',
   readOnly: false,
   startOfPeriod: true,
   id: null,
   label: null,
   useRow: false,
   useButton: false
};

export default DateSelector;
